/* PDP UI only. Shopify's existing integration owns all ecommerce events. */
(() => {
  'use strict';
  if (window.__caenePdpTracking) return;
  window.__caenePdpTracking = true;
  const keys = ['product_id', 'product_handle', 'variant_id', 'variant_name', 'purchase_mode',
    'selling_plan_id', 'selling_plan_name', 'previous_purchase_mode', 'previous_variant_id',
    'template', 'component', 'action', 'placement', 'availability', 'validation_type',
    'content_key', 'destination_type', 'destination_group', 'scroll_percent'];
  const components = new Set(['purchase', 'variant_picker', 'media', 'accordion', 'faq', 'reviews',
    'subscription_info', 'trust_shipping', 'related_products', 'header', 'footer', 'content', 'page']);
  const contentKeys = new Set(['ingredients', 'how_to_use', 'reviews', 'subscription_terms',
    'shipping', 'trust', 'faq', 'benefits', 'supplement_facts', 'research', 'media']);
  const actions = new Set(['open', 'close', 'next_media', 'previous_media', 'play', 'expand']);
  let root, context, previous, observer, frame, lastAttempt, clarityContextRoot;
  let stickySent = false;
  const depths = new Set();
  const disclosures = new WeakMap();

  function permitted() {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
    catch (_) { return false; }
  }
  function form() { return root?.querySelector('[data-track-form="main"]'); }
  function selection() {
    const f = form();
    let variantId = String(f?.elements.namedItem('id')?.value || '');
    const input = f?.elements.namedItem('selling_plan');
    let planId = input && !input.disabled ? String(input.value || '') : '';
    let requestedMode = null;
    if (!f) {
      variantId = String(root?.dataset.variantId || '');
      const choice = root?.querySelector('[data-track-purchase][type="radio"]:checked')?.value;
      requestedMode = choice === 'subscription' ? 'subscription' : choice === 'once' ? 'one_time' : null;
      if (requestedMode === 'subscription') {
        const interval = String(root?.querySelector('[data-delivery-interval]')?.value || '30');
        planId = String(root?.getAttribute(`data-selling-plan-${interval}`) || '');
      }
    }
    const variant = context?.variants.find(v => v.id === variantId);
    const plan = context?.plans.find(p => p.id === planId);
    // Unknown/unresolved selections must never be reported as one-time purchases.
    const purchase = root?.querySelector('caene-purchase');
    const mode = !variant || (purchase && purchase.canPurchase === false) || (requestedMode === 'subscription' && !plan) ? 'unknown' :
      planId ? (plan ? 'subscription' : 'unknown') :
      requestedMode || (context?.requiresPlan ? 'unknown' : 'one_time');
    return {variantId, planId, variant, plan, mode};
  }
  function syncClarity(selected = selection()) {
    if (!root?.isConnected || !context || !permitted() || typeof window.clarity !== 'function') return;
    if (clarityContextRoot !== root) {
      window.clarity('set', 'pdp_product', context.handle);
      window.clarity('set', 'pdp_template', context.template);
      clarityContextRoot = root;
    }
    window.clarity('set', 'pdp_purchase_mode', selected.mode);
  }
  function emit(event, data = {}, selected = selection()) {
    if (!root?.isConnected || !context || !permitted()) return false;
    syncClarity(selected);
    // Clear every owned parameter on every event. Never touch ecommerce or other namespaces.
    const payload = Object.fromEntries(keys.map(k => [`pdp_${k}`, null]));
    Object.assign(payload, {
      event, pdp_product_id: context.productId, pdp_product_handle: context.handle,
      pdp_variant_id: selected.variantId || null, pdp_purchase_mode: selected.mode,
      pdp_selling_plan_id: selected.planId || null, pdp_template: context.template,
      ...data,
    });
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    if (typeof window.clarity === 'function') window.clarity('event', event);
    return true;
  }
  function component(element) {
    const value = element?.closest('[data-track-component]')?.dataset.trackComponent;
    return components.has(value) ? value : 'content';
  }
  function content(element, action) {
    const key = element?.closest('[data-track-content-key]')?.dataset.trackContentKey;
    if (!contentKeys.has(key) || !actions.has(action)) return;
    emit('pdp_content_interaction', {pdp_component: component(element), pdp_action: action,
      pdp_content_key: key, pdp_placement: 'main'});
  }
  function compareSelection() {
    if (!root?.isConnected) return;
    const next = selection();
    if (previous && next.variantId && next.variant && next.variantId !== previous.variantId) {
      emit('pdp_variant_select', {pdp_component: 'variant_picker', pdp_action: 'select',
        pdp_placement: 'main', pdp_variant_name: next.variant.name,
        pdp_previous_variant_id: previous.variantId || null,
        pdp_availability: next.variant.available ? 'in_stock' : 'out_of_stock'}, next);
    }
    if (previous && (next.mode !== previous.mode || next.planId !== previous.planId)) {
      emit('pdp_purchase_option_select', {pdp_component: 'purchase', pdp_action: 'select',
        pdp_placement: 'main', pdp_selling_plan_name: next.plan?.name || null,
        pdp_previous_purchase_mode: previous.mode}, next);
    }
    previous = next; // Also update while consent is denied; never replay past actions.
  }
  function visible(element) {
    if (!element || !element.getClientRects().length) return false;
    if (element.closest('[hidden], [aria-hidden="true"]')) return false;
    if (element.checkVisibility && !element.checkVisibility({checkOpacity:true, checkVisibilityCSS:true})) return false;
    const r = element.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < innerHeight && r.right > 0 && r.left < innerWidth;
  }
  function checkSticky() {
    if (stickySent || !permitted()) return;
    const button = root?.querySelector('[data-track-sticky] [data-track-atc], [data-track-sticky][data-track-atc]');
    if (visible(button)) stickySent = emit('pdp_sticky_atc_view', {pdp_component:'purchase', pdp_action:'view', pdp_placement:'sticky'});
  }
  function scheduleSticky() {
    if (frame) return;
    frame = requestAnimationFrame(() => {frame = null; checkSticky();});
  }
  function navigation(anchor) {
    const raw = anchor.getAttribute('href');
    if (!raw || raw.startsWith('#')) return;
    let url;
    try { url = new URL(raw, location.href); } catch (_) { return; }
    if (!['https:', 'http:', 'mailto:', 'tel:'].includes(url.protocol)) return;
    const path = url.pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, '') || '/';
    let type = 'other';
    if (url.origin !== location.origin) type = 'external';
    else if (url.pathname === location.pathname && url.hash && url.search === location.search) return;
    else if (/\/products\//.test(path)) type = 'product';
    else if (/^\/collections(?:\/|$)/.test(path)) type = 'collection';
    else if (/^\/cart(?:\/|$)/.test(path)) type = 'cart';
    else if (/^\/search(?:\/|$)/.test(path)) type = 'search';
    else if (/^\/account(?:\/|$)/.test(path)) type = 'account';
    else if (path === '/') type = 'homepage';
    else if (/^\/(pages|policies|blogs)(?:\/|$)/.test(path)) type = 'content';
    const group = type === 'external' ? 'external' :
      path.startsWith('/policies/') ? 'policies' : type;
    emit('pdp_navigation_click', {pdp_component:component(anchor), pdp_action:'navigate',
      pdp_destination_type:type, pdp_destination_group:group});
  }
  function validation(type, placement) {
    emit('pdp_validation_error', {pdp_component:'purchase', pdp_action:'error',
      pdp_placement:placement, pdp_validation_type:type});
  }
  function init() {
    const nextRoot = document.querySelector('[data-track-pdp]');
    if (nextRoot === root && root?.isConnected) { scheduleSticky(); return; }
    observer?.disconnect();
    root = nextRoot;
    if (!root) return;
    try { context = JSON.parse(root.querySelector('[data-track-context]').textContent); }
    catch (_) { root = null; return; }
    previous = selection();
    syncClarity(previous);
    root.querySelectorAll('details[data-track-content-key]').forEach(d => disclosures.set(d,d.open));
    observer = new MutationObserver(scheduleSticky);
    observer.observe(root,{subtree:true, childList:true, attributes:true, attributeFilter:['class','style','hidden','aria-hidden','disabled']});
    scheduleSticky();
  }
  document.addEventListener('change', event => {
    if (!root?.contains(event.target)) return;
    if (event.target.closest('[data-track-purchase], [data-track-variant]') ||
        event.target.matches('[name="id"], [name="selling_plan"]')) {
      // Native purchase controls synchronize hidden form fields in their change handler.
      queueMicrotask(compareSelection);
    }
  });
  document.addEventListener('click', event => {
    if (!root?.isConnected || !(event.target instanceof Element)) return;
    const button = event.target.closest('[data-track-atc]');
    if (button && root.contains(button)) {
      if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      const mainButton = form()?.querySelector('[data-track-atc]');
      if (mainButton?.disabled || mainButton?.getAttribute('aria-disabled') === 'true') return;
      const placement = button.dataset.trackPlacement === 'sticky' ? 'sticky' : 'main';
      lastAttempt = {placement, at:performance.now(), variantId:selection().variantId, pending:false};
      emit('pdp_atc_click', {pdp_component:'purchase', pdp_action:'click', pdp_placement:placement});
      return;
    }
    const control = event.target.closest('[data-track-action]');
    if (control && !control.disabled && control.getAttribute('aria-disabled') !== 'true' && control.getAttribute('aria-current') !== 'true') {
      let action = control.dataset.trackAction;
      if (action === 'expand' && control.getAttribute('aria-expanded') === 'true') action = 'close';
      // A reel's playback control also pauses. Do not mislabel a pause as play.
      const video = control.hasAttribute('data-playback') ? control.parentElement.querySelector('video') : null;
      if (!(action === 'play' && video && !video.paused)) content(control, action);
    }
    const anchor = event.target.closest('a[href]');
    if (anchor) navigation(anchor);
  }, true);
  document.addEventListener('submit', event => {
    if (event.target === form()) {
      const placement = event.submitter?.dataset.trackPlacement === 'sticky' ? 'sticky' : 'main';
      const selected = selection();
      lastAttempt = {placement, at:performance.now(), variantId:selected.variantId, pending:true};
      const type = !selected.variant ? 'missing_variant' : !selected.variant.available ? 'out_of_stock' :
        selected.mode === 'unknown' ? 'missing_purchase_option' : null;
      if (type) { validation(type,placement); lastAttempt.pending = false; }
    } else if (event.target.matches('form[role="search"]')) {
      emit('pdp_navigation_click',{pdp_component:component(event.target),pdp_action:'navigate',
        pdp_destination_type:'search',pdp_destination_group:'search'});
    }
  }, true);
  document.addEventListener('toggle', event => {
    const target = event.target;
    if (!target.matches?.('details[data-track-content-key]')) return;
    const before = disclosures.get(target) ?? false;
    disclosures.set(target,target.open);
    if (before !== target.open) content(target,target.open ? 'open' : 'close');
  }, true);
  document.addEventListener('play', event => {
    if (event.target.matches?.('video[data-track-content-key]') && !event.target.autoplay) content(event.target,'play');
  }, true);
  window.addEventListener('scroll', () => {
    scheduleSticky();
    if (!permitted() || !root?.isConnected) return;
    const height = document.documentElement.scrollHeight;
    if (height <= innerHeight || scrollY <= 0) return;
    const percent = Math.min(100, (scrollY + innerHeight) / height * 100);
    [25,50,75,90].forEach(depth => {
      if (percent >= depth && !depths.has(depth) && emit('pdp_scroll_depth',
        {pdp_component:'page',pdp_action:'scroll',pdp_scroll_percent:depth})) depths.add(depth);
    });
  }, {passive:true});
  window.addEventListener('resize',scheduleSticky,{passive:true});
  document.addEventListener('visitorConsentCollected', () => {
    previous = selection();
    syncClarity(previous);
    scheduleSticky();
  });
  document.addEventListener('shopify:section:load',init);
  document.addEventListener('shopify:section:unload', () => queueMicrotask(init));
  window.addEventListener('pageshow',scheduleSticky);
  function boot() {
    init();
    if (!window.Shopify?.customerPrivacy && window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.1'}],()=>scheduleSticky());
    }
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartError, event => {
        if (event.source !== 'product-form' || !lastAttempt?.pending ||
            String(event.productVariantId) !== lastAttempt.variantId || performance.now()-lastAttempt.at > 30000) return;
        validation('other', lastAttempt.placement); // Never send server/free-text errors.
        lastAttempt.pending = false;
      });
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {if(lastAttempt) lastAttempt.pending=false;});
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',boot,{once:true});
  else boot();
})();
